import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useGetSstv2MigrationAttemptStateQuery } from "src/features/partners/partnersApi"
import { Sstv2MigrationCallout } from "./Sstv2MigrationCallout"
import {
  flipSeverity,
  flipVerdict,
  flipVerdictHintKey,
  flipVerdictLabelKey,
  rollbackOutcome,
} from "./sstv2AttemptState"
import { Sstv2MigrationRow } from "./sstv2MigrationTypes"

const labelSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
}

/** Polling cadence while the drawer is open. Scoped to this panel only. */
const POLL_INTERVAL_MS = 15000

/** One `label   value` line, right-aligned like the drawer's other detail rows. */
const StateRow: FC<{
  label: string
  hint?: string
  value: React.ReactNode
}> = ({ label, hint, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    {hint ? (
      <Tooltip title={hint}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            borderBottom: "1px dotted",
            borderColor: "divider",
            cursor: "help",
          }}
        >
          {label}
        </Typography>
      </Tooltip>
    ) : (
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    )}
    <Box sx={{ textAlign: "right" }}>{value}</Box>
  </Stack>
)

const Num: FC<{ value: number; strong?: boolean }> = ({ value, strong }) => (
  <Typography
    variant="body2"
    fontFamily="monospace"
    fontWeight={strong ? 700 : 400}
  >
    {value}
  </Typography>
)

/**
 * ATTEMPT STATE — the panel that makes a migration monitorable (DEV-193).
 *
 * Everything above this in the drawer is derived from the job record, which records
 * what the PARTNER told us. This panel is the only place that reports what we
 * actually DID, and it exists because the cutover-atomic redesign moved every
 * interesting failure out of the counters:
 *
 *  1. **Is a durable flip alive?** An attempt at `OPERATIONAL` with unflipped
 *     mappings and no live execution is the stalled state the 15-minute tick
 *     restarts. `processingState: COMPLETE` is silent about it.
 *  2. **The mapping collection.** The mapping items ARE the work set, so "how much
 *     is left" is a property of that collection and not of any counter.
 *  3. **Rollback outcome.** Restored rows are LIVE; only adopted rows were
 *     archived. One combined number would report a correct rollback as data loss.
 *
 * Read-only: the backend route has `ListDurableExecutionsByFunction` and nothing
 * else, so opening this drawer can never start, restart or cancel a flip. Restarting
 * a stalled flip stays with the reconcile tick, which owns that on a schedule.
 */
export const Sstv2MigrationAttemptStatePanel: FC<{
  row: Sstv2MigrationRow
}> = ({ row }) => {
  const { t } = useTranslation()

  // A pre-2026-07-28 record has no job id, so there is no attempt to ask about.
  // Skipping is right: querying a partition built from `undefined` would return
  // nothing and render as a healthy, fully-flipped attempt.
  const skip = !row.tenantId || !row.migrationId
  const query = useGetSstv2MigrationAttemptStateQuery(
    { tenantId: row.tenantId, migrationId: row.migrationId ?? "" },
    { skip, pollingInterval: POLL_INTERVAL_MS, skipPollingIfUnfocused: true },
  )

  const state = query.data?.data
  const verdict = useMemo(() => flipVerdict(row, state), [row, state])
  const severity = flipSeverity(verdict)
  const rollback = useMemo(() => rollbackOutcome(row, state), [row, state])

  if (skip) {
    return (
      <Box>
        <Typography sx={{ ...labelSx, mb: 1 }}>
          {t("migrationAttemptState")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("migrationAttemptStateNoJobId")}
        </Typography>
      </Box>
    )
  }

  return (
    <Box data-ui="UI-SL2W-258">
      <Typography sx={{ ...labelSx, mb: 1 }}>
        {t("migrationAttemptState")}
      </Typography>

      {query.isLoading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary">
            {t("migrationAttemptStateLoading")}
          </Typography>
        </Stack>
      )}

      {query.isError && (
        <Sstv2MigrationCallout
          severity="advisory"
          title={t("migrationAttemptStateError")}
          hint={t("migrationAttemptStateErrorHint")}
        />
      )}

      {state && (
        <Stack spacing={2}>
          {/* ── 1. Flip execution liveness ── */}
          {severity !== "none" && (
            <Sstv2MigrationCallout
              severity={severity}
              title={t(flipVerdictLabelKey(verdict))}
              hint={t(flipVerdictHintKey(verdict))}
            />
          )}

          <Stack spacing={1.25}>
            <StateRow
              label={t("migrationFlipExecution")}
              hint={t("migrationFlipExecutionHint")}
              value={
                <Chip
                  size="small"
                  variant="outlined"
                  color={
                    severity === "severe"
                      ? "error"
                      : state.flip.flipRunning || state.flip.rollbackRunning
                        ? "primary"
                        : "default"
                  }
                  label={t(flipVerdictLabelKey(verdict))}
                />
              }
            />

            {/* When it last started, and how many times it has been restarted.
                Read from the platform's execution registry, NOT from a stamped
                field: nothing persists a restart timestamp, because
                `DurableExecutionName` dedup deliberately replaced the DDB latch
                that would otherwise have provided one (a latch left set by a dead
                run would block the reconciler's restart). An empty history means
                "could not read", so it is rendered as unknown rather than zero. */}
            {state.flip.lastStartedAt && (
              <StateRow
                label={t("migrationFlipLastStarted")}
                hint={t("migrationFlipLastStartedHint")}
                value={
                  <Typography variant="body2" fontFamily="monospace">
                    {state.flip.lastStartedAt.replace("T", " ").slice(0, 19)} UTC
                  </Typography>
                }
              />
            )}
            {state.flip.restartCount > 0 && (
              <StateRow
                label={t("migrationFlipRestarts")}
                hint={t("migrationFlipRestartsHint")}
                value={<Num value={state.flip.restartCount} strong />}
              />
            )}

            {/* ── 2. The mapping collection ── */}
            <StateRow
              label={t("migrationMappingsActive")}
              hint={t("migrationMappingsActiveHint")}
              value={
                <Typography variant="body2" fontFamily="monospace">
                  {state.active.totalExact
                    ? state.active.total
                    : `≥ ${state.active.total}`}
                </Typography>
              }
            />
            <StateRow
              label={t("migrationMappingsHistory")}
              hint={t("migrationMappingsHistoryHint")}
              value={
                <Typography variant="body2" fontFamily="monospace">
                  {state.history.totalExact
                    ? state.history.total
                    : `≥ ${state.history.total}`}
                </Typography>
              }
            />
            {/* ⚠ A KNOWN BLIND SPOT, stated rather than left to be inferred.
                The backend read path returns a full rollup (liveOnV2 / liveOnV1 /
                absent) for ACTIVE but only a COUNT for history — correct under D5,
                when history was never flipped and there was nothing to classify.
                Since D37 history IS flipped, so this panel can say how far the
                active walk got but only how many history mappings exist. The job
                record's history progress bar is the only signal for the rest.
                Widening it is a backend change (`Sstv2MappingRollup` on the history
                field) — see the note on `Sstv2MigrationAttemptState`. */}
            {state.history.total > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t("migrationMappingsHistoryNoRollup")}
              </Typography>
            )}

            {/* Forward attempts read the buckets as progress; rolled-back ones read
                them as an unwind outcome, so only one of the two blocks renders. */}
            {!rollback && (
              <>
                <StateRow
                  label={t("migrationMappingsFlipped")}
                  hint={t("migrationMappingsFlippedHint")}
                  value={<Num value={state.active.liveOnV2} strong />}
                />
                {state.active.liveOnV1 > 0 && (
                  <StateRow
                    label={t("migrationMappingsUnflipped")}
                    hint={t("migrationMappingsUnflippedHint")}
                    value={<Num value={state.active.liveOnV1} strong />}
                  />
                )}
                {state.active.absent > 0 && (
                  <StateRow
                    label={t("migrationMappingsUnresolved")}
                    hint={t("migrationMappingsUnresolvedHint")}
                    value={<Num value={state.active.absent} strong />}
                  />
                )}
              </>
            )}

            {/* ── 3. Rollback outcome: restored is NOT deleted ── */}
            {rollback && (
              <>
                <StateRow
                  label={t("migrationRollbackRestored")}
                  hint={t("migrationRollbackRestoredHint")}
                  value={<Num value={rollback.restored} strong />}
                />
                <StateRow
                  label={t("migrationRollbackArchived")}
                  hint={t("migrationRollbackArchivedHint")}
                  value={<Num value={rollback.archived} strong />}
                />
                {rollback.incomplete && (
                  <StateRow
                    label={t("migrationRollbackStillOnV2")}
                    hint={t("migrationRollbackStillOnV2Hint")}
                    value={<Num value={rollback.stillOnV2} strong />}
                  />
                )}
              </>
            )}
          </Stack>

          {/* A sample is never presented as the whole set. */}
          {state.active.truncated && (
            <Typography variant="caption" color="text.secondary">
              {t("migrationMappingsSampled", {
                classified: state.active.classified,
                total: state.active.total,
              })}
            </Typography>
          )}

        </Stack>
      )}
    </Box>
  )
}

export default Sstv2MigrationAttemptStatePanel
